<template>
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-button class="button_back" @click="goBack">
        <ion-icon name="arrow-back-outline"></ion-icon>
      </ion-button>
    </ion-buttons>
  </ion-toolbar>

  <div class="main_wrapper ion-padding">
    <div class="container">
      <!-- Header -->
      <div class="auth-header">
        <div class="auth-heading">
          <h1>Acknowledged Agreements</h1>
          <p class="auth-subtext">View all documents and agreements you have accepted, organized by group.</p>
        </div>
        <div class="brand-icon">
          <SvgIcon name="brandIcon" width="32" height="32" viewBox="0 0 32 32" fill="none" />
        </div>
      </div>

      <!-- Search / Filter Bar (if groups exist) -->
      <div class="agreements-search-box mb-3" v-if="groupsList && groupsList.length > 0">
        <el-input
          v-model="searchQuery"
          placeholder="Search by group name, document, or address..."
          prefix-icon="el-icon-search"
          clearable
          class="custom-search-input"
        />
      </div>

      <!-- Stats Pill Bar -->
      <div class="agreements-stats-bar" v-if="!isLoading && totalDocumentsCount > 0">
        <span class="stat-item">
          <i class="el-icon-office-building"></i> <strong>{{ filteredGroups.length }}</strong> {{ filteredGroups.length === 1 ? 'Group' : 'Groups' }}
        </span>
        <span class="stat-divider">•</span>
        <span class="stat-item">
          <i class="el-icon-document-checked"></i> <strong>{{ totalDocumentsCount }}</strong> {{ totalDocumentsCount === 1 ? 'Agreement' : 'Agreements' }} Accepted
        </span>
      </div>

      <!-- Loading State -->
      <div class="agreements-loading-wrapper" v-if="isLoading">
        <div class="skeleton-group-card" v-for="i in 2" :key="i">
          <div class="skeleton-group-header">
            <div class="skeleton-icon"></div>
            <div class="skeleton-header-text">
              <div class="skeleton-line skeleton-title"></div>
              <div class="skeleton-line skeleton-sub"></div>
            </div>
          </div>
          <div class="skeleton-doc-row" v-for="j in 2" :key="j">
            <div class="skeleton-doc-icon"></div>
            <div class="skeleton-doc-info">
              <div class="skeleton-line skeleton-doc-name"></div>
              <div class="skeleton-line skeleton-doc-meta"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div class="agreements-empty-card" v-else-if="filteredGroups.length === 0">
        <div class="empty-icon-circle">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 11L11 13L15 9" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M22 10V15C22 20 20 22 15 22H9C4 22 2 20 2 15V9C2 4 4 2 9 2H14" stroke="#64748b" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M22 10H18C15 10 14 9 14 6V2L22 10Z" stroke="#64748b" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="empty-title">No Acknowledged Agreements</div>
        <div class="empty-subtitle">
          <template v-if="searchQuery">
            No agreements match your search for "{{ searchQuery }}". Try a different keyword.
          </template>
          <template v-else>
            You haven't acknowledged any onboarding or group agreements yet. When you accept internal pool or group manager documents, they will be organized by group here.
          </template>
        </div>
      </div>

      <!-- Group-wise Agreements List -->
      <div class="groups-container" v-else>
        <div 
          v-for="(group, gIdx) in filteredGroups" 
          :key="group.id || gIdx"
          class="group-card"
        >
          <!-- Group Header -->
          <div class="group-header">
            <div class="group-header-main">
              <div class="group-avatar-icon">
                <i class="el-icon-office-building"></i>
              </div>
              <div class="group-title-info">
                <div class="group-name-row">
                  <h3 class="group-name">{{ group.name }}</h3>
                  <span class="group-doc-count-badge">
                    {{ group.documents.length }} {{ group.documents.length === 1 ? 'Agreement' : 'Agreements' }}
                  </span>
                </div>
                
                <!-- Group Details / Address / Contact -->
                <div class="group-meta-row" v-if="group.address || group.email || group.phone">
                  <span v-if="group.address" class="group-meta-item group-address">
                    <i class="el-icon-location-outline"></i> {{ group.address }}
                  </span>
                  <span v-if="group.email" class="group-meta-item group-email">
                    <i class="el-icon-message"></i> {{ group.email }}
                  </span>
                  <span v-if="group.phone" class="group-meta-item group-phone">
                    <i class="el-icon-phone-outline"></i> {{ group.phone }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Group Documents List -->
          <div class="group-docs-list">
            <div 
              v-for="(doc, dIdx) in group.documents" 
              :key="doc.id || dIdx"
              class="doc-item-row"
            >
              <div class="doc-item-main" @click="openDocPreview(doc)">
                <!-- Icon -->
                <div 
                  class="doc-item-icon" 
                  :class="{ 'is-pdf': isPdfDoc(doc.url || doc.extension), 'is-img': isImageDoc(doc.url || doc.extension) }"
                >
                  <i :class="isPdfDoc(doc.url || doc.extension) ? 'el-icon-document' : isImageDoc(doc.url || doc.extension) ? 'el-icon-picture-outline' : 'el-icon-document-checked'"></i>
                </div>

                <!-- Info -->
                <div class="doc-item-info">
                  <div class="doc-title-row">
                    <span class="doc-name">{{ doc.name }}</span>
                    <span class="doc-badge-accepted">
                      <i class="el-icon-check"></i> Accepted
                    </span>
                    <span 
                      v-if="doc.is_deleted_by_group || doc.is_deleted || doc.group_file_status === 'deleted'" 
                      class="doc-badge-removed"
                      title="This document was removed from active requirements by the pharmacy group"
                    >
                      <i class="el-icon-warning-outline"></i> Removed by Group
                    </span>
                  </div>

                  <div class="doc-meta-row">
                    <span v-if="doc.extension" class="meta-tag meta-ext">
                      {{ doc.extension.toUpperCase() }}
                    </span>
                    <span v-if="doc.acknowledged_at || doc.created_at || doc.updated_at" class="meta-tag meta-date">
                      <i class="el-icon-date"></i> Accepted on {{ formatDate(doc.acknowledged_at || doc.updated_at || doc.created_at) }}
                    </span>
                    <span v-if="(doc.is_deleted_by_group || doc.is_deleted) && doc.deleted_at" class="meta-tag meta-removed-date">
                      <i class="el-icon-time"></i> Removed on {{ formatDate(doc.deleted_at) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Small Professional Actions -->
              <div class="doc-item-actions">
                <button 
                  type="button" 
                  class="btn-doc-small btn-view"
                  @click="openDocPreview(doc)"
                  v-if="doc.url && doc.url !== '='"
                  title="View agreement"
                >
                  <i class="el-icon-view"></i>
                  <span>View</span>
                </button>
                <button 
                  type="button" 
                  class="btn-doc-small btn-download"
                  :disabled="downloadingDocId === (doc.id || doc.name)"
                  @click="downloadDoc(doc)"
                  v-if="doc.url && doc.url !== '='"
                  title="Download agreement"
                >
                  <i :class="downloadingDocId === (doc.id || doc.name) ? 'el-icon-loading' : 'el-icon-download'"></i>
                  <span>Download</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-overlay"></div>
    </div>
  </div>

  <div class="background-circles">
    <div class="circle circle-2"></div>
    <div class="circle circle-1"></div>
    <SvgIcon name="bgIcon" class="bg-icon" width="174" height="174" viewBox="0 0 174 174" fill="none" />
  </div>

  <!-- Document Preview Dialog Modal -->
  <el-dialog
    v-model="showDocPreviewModal"
    :title="selectedDocForReview ? selectedDocForReview.name : 'Document Preview'"
    width="92%"
    custom-class="doc-review-dialog modal_success"
    :destroy-on-close="true"
    :close-on-click-modal="true"
  >
    <div v-if="selectedDocForReview" class="doc-modal-content">
      <!-- Modal Header Meta Bar -->
      <div class="doc-modal-meta-bar">
        <div class="doc-modal-tags">
          <span class="doc-badge-accepted"><i class="el-icon-check"></i> Accepted Agreement</span>
          <span 
            v-if="selectedDocForReview.is_deleted_by_group || selectedDocForReview.is_deleted || selectedDocForReview.group_file_status === 'deleted'" 
            class="doc-badge-removed"
          >
            <i class="el-icon-warning-outline"></i> Removed by Group
          </span>
          <span v-if="selectedDocForReview.group_name" class="meta-item meta-group">
            <i class="el-icon-office-building"></i> {{ selectedDocForReview.group_name }}
          </span>
          <span v-if="selectedDocForReview.extension" class="meta-item meta-ext">
            {{ selectedDocForReview.extension.toUpperCase() }}
          </span>
        </div>
        <div class="doc-modal-header-actions" v-if="selectedDocForReview.url && selectedDocForReview.url !== '='">
          <button 
            type="button" 
            class="btn-modal-action btn-download"
            :disabled="isDownloadingModalDoc"
            @click="downloadDoc(selectedDocForReview, true)"
          >
            <i :class="isDownloadingModalDoc ? 'el-icon-loading' : 'el-icon-download'"></i>
            {{ isDownloadingModalDoc ? 'Downloading...' : 'Download' }}
          </button>
          <a 
            :href="selectedDocForReview.url" 
            target="_blank" 
            rel="noopener noreferrer" 
            class="btn-modal-action btn-open-tab"
          >
            <i class="el-icon-top-right"></i> Open in Tab
          </a>
        </div>
      </div>

      <!-- Notice if document was removed by group -->
      <div 
        v-if="selectedDocForReview.is_deleted_by_group || selectedDocForReview.is_deleted || selectedDocForReview.group_file_status === 'deleted'" 
        class="doc-modal-removed-notice"
      >
        <i class="el-icon-warning-outline"></i>
        <div class="notice-text">
          <strong>Removed by Group:</strong> This agreement was removed from active onboarding requirements by the group<span v-if="selectedDocForReview.deleted_at"> on {{ formatDate(selectedDocForReview.deleted_at) }}</span>. Your signed record is preserved here for legal and compliance reasons.
        </div>
      </div>

      <!-- Inline Document Preview Box -->
      <div class="doc-inline-preview-wrapper" v-if="selectedDocForReview.url && selectedDocForReview.url !== '='">
        <!-- Image Preview -->
        <div v-if="isImageDoc(selectedDocForReview.url || selectedDocForReview.extension)" class="doc-preview-container doc-preview-image-box">
          <img 
            :src="selectedDocForReview.url" 
            :alt="selectedDocForReview.name" 
            class="doc-preview-image" 
          />
        </div>

        <!-- PDF / Frame Preview -->
        <div v-else class="doc-preview-container doc-preview-frame-box">
          <iframe 
            :src="selectedDocForReview.url" 
            class="doc-preview-iframe"
            title="Agreement Preview"
            frameborder="0"
          ></iframe>
        </div>
      </div>

      <!-- No Preview available fallback -->
      <div class="doc-no-preview-available" v-else>
        <i class="el-icon-document text-muted"></i>
        <p>No preview link is available for this document.</p>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer flex-footer">
        <el-button @click="showDocPreviewModal = false" class="el-button--blue">Close</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { ref, computed, onMounted, inject } from "vue";
import { addIcons } from "ionicons";
import { arrowBackOutline } from "ionicons/icons";
import { IonToolbar, IonButton, IonButtons, IonIcon } from "@ionic/vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import moment from "moment";
import { ElMessage } from "@/components/Vendor/Element";
import SvgIcon from "@/components/Svg/SvgIcon.vue";

addIcons({
  "arrow-back-outline": arrowBackOutline,
});

export default {
  name: "AcknowledgedAgreements",
  components: {
    IonToolbar,
    IonButton,
    IonButtons,
    IonIcon,
    SvgIcon,
  },
  setup() {
    const $store = useStore();
    const $router = useRouter();
    const setPageClassGlobal = inject("setPageClassGlobal");

    const isLoading = ref(true);
    const groupsList = ref([]);
    const searchQuery = ref("");
    const showDocPreviewModal = ref(false);
    const selectedDocForReview = ref(null);
    const downloadingDocId = ref(null);
    const isDownloadingModalDoc = ref(false);

    const goBack = () => {
      if (window.history.length > 1) {
        $router.back();
      } else {
        $router.push({ name: "profile" });
      }
    };

    const isGenericDefaultName = (name) => {
      if (!name || typeof name !== "string") return true;
      const lower = name.toLowerCase().trim();
      return (
        lower === "" ||
        lower === "document" ||
        lower === "documents" ||
        lower === "default" ||
        lower === "default document" ||
        lower === "default agreement" ||
        lower === "agreement" ||
        lower === "agreements" ||
        lower === "group agreement" ||
        lower === "group agreements" ||
        lower === "group document" ||
        lower === "group documents" ||
        lower === "onboarding agreement" ||
        lower === "onboarding document" ||
        lower === "null" ||
        lower === "undefined" ||
        lower === "sample" ||
        lower === "file"
      );
    };

    const extractDocumentName = (doc, groupName = "") => {
      if (!doc) return "Agreement";
      if (typeof doc === "string" && doc.trim()) {
        const s = doc.trim();
        if (!isGenericDefaultName(s)) return s;
      }

      // Check all direct keys on doc object
      const directCandidates = [
        doc.document_name,
        doc.documentName,
        doc.doc_name,
        doc.docName,
        doc.document_title,
        doc.documentTitle,
        doc.doc_title,
        doc.docTitle,
        doc.user_friendly_name,
        doc.userFriendlyName,
        doc.user_friendly_document_name,
        doc.userFriendlyDocumentName,
        doc.file_name,
        doc.fileName,
        doc.filename,
        doc.original_filename,
        doc.original_name,
        doc.originalName,
        doc.display_name,
        doc.displayName,
        doc.title,
        doc.agreement_name,
        doc.agreementName,
        doc.label,
        doc.heading,
        doc.onboarding_document_name,
        doc.group_document_name,
      ];

      for (const n of directCandidates) {
        if (n && typeof n === "string" && !isGenericDefaultName(n)) {
          const trimmed = n.trim();
          if (!groupName || trimmed.toLowerCase() !== groupName.toLowerCase().trim()) {
            return trimmed;
          }
        }
      }

      // Check nested objects
      const nestedObjects = [
        doc.document,
        doc.onboarding_document,
        doc.onboardingDocument,
        doc.group_document,
        doc.groupDocument,
        doc.group_manager_document,
        doc.groupManagerDocument,
        doc.shift_document,
        doc.shiftDocument,
      ];

      for (const nested of nestedObjects) {
        if (nested) {
          if (typeof nested === "string" && !isGenericDefaultName(nested)) {
            return nested.trim();
          }
          if (typeof nested === "object") {
            const nestedCandidates = [
              nested.document_name,
              nested.documentName,
              nested.doc_name,
              nested.docName,
              nested.user_friendly_name,
              nested.userFriendlyName,
              nested.user_friendly_document_name,
              nested.title,
              nested.file_name,
              nested.fileName,
              nested.filename,
              nested.original_filename,
              nested.original_name,
              nested.name,
              nested.display_name,
              nested.label,
            ];
            for (const n of nestedCandidates) {
              if (n && typeof n === "string" && !isGenericDefaultName(n)) {
                const trimmed = n.trim();
                if (!groupName || trimmed.toLowerCase() !== groupName.toLowerCase().trim()) {
                  return trimmed;
                }
              }
            }
          }
        }
      }

      // Check doc.name only if it doesn't match groupName and not generic
      if (doc.name && typeof doc.name === "string" && !isGenericDefaultName(doc.name)) {
        const trimmed = doc.name.trim();
        if (!groupName || trimmed.toLowerCase() !== groupName.toLowerCase().trim()) {
          return trimmed;
        }
      }

      // Try URL / file extraction
      const urlCandidates = [
        doc.url,
        doc.document_url,
        doc.file_url,
        doc.link,
        doc.file,
        doc.file_path,
        doc.path,
        doc.document?.url,
        doc.onboarding_document?.url,
        doc.group_document?.url,
      ];

      for (const u of urlCandidates) {
        if (u && typeof u === "string" && u.includes("/") && u !== "=") {
          try {
            const cleanUrl = u.split("?")[0];
            const filename = cleanUrl.substring(cleanUrl.lastIndexOf("/") + 1);
            if (filename && filename.length > 2) {
              const decoded = decodeURIComponent(filename);
              const nameWithoutExt = decoded.replace(/\.[^/.]+$/, "");
              if (nameWithoutExt && !isGenericDefaultName(nameWithoutExt)) {
                return nameWithoutExt.replace(/[-_]/g, " ").trim();
              }
            }
          } catch (e) {
            // ignore
          }
        }
      }

      // If document ID or tracking ID is available, provide a clean identifiable label
      const docId = doc.document_id || doc.id || doc.history_id || doc.acceptance_tracking_id;
      if (docId) {
        return `Agreement #${docId}`;
      }

      return "Agreement";
    };

    const extractGroupName = (item) => {
      if (!item) return "Group";
      const possibleGroupNames = [
        item.group_name,
        item.groupname,
        item.groupName,
        item.pharmacy_name,
        item.pharmacyName,
        item.employer_name,
        item.employerName,
        item.name,
        item.title,
      ];

      for (const g of possibleGroupNames) {
        if (g && typeof g === "string" && g.trim() !== "" && !isGenericDefaultName(g)) {
          return g.trim();
        }
      }

      return "Group";
    };

    const normalizeGroups = (data) => {
      if (!data) return [];
      let rawGroups = [];

      if (data.documents && Array.isArray(data.documents)) {
        rawGroups = data.documents;
      } else if (data.data && Array.isArray(data.data.documents)) {
        rawGroups = data.data.documents;
      } else if (data.data && data.data.data && Array.isArray(data.data.data.documents)) {
        rawGroups = data.data.data.documents;
      } else if (data.acknowledged_documents && Array.isArray(data.acknowledged_documents)) {
        rawGroups = data.acknowledged_documents;
      } else if (data.data && Array.isArray(data.data.acknowledged_documents)) {
        rawGroups = data.data.acknowledged_documents;
      } else if (data.data && Array.isArray(data.data)) {
        rawGroups = data.data;
      } else if (Array.isArray(data)) {
        rawGroups = data;
      } else if (data.data && typeof data.data === "object") {
        if (Array.isArray(data.data.documents)) {
          rawGroups = data.data.documents;
        } else {
          rawGroups = [data.data];
        }
      } else if (typeof data === "object") {
        rawGroups = [data];
      }

      const groups = [];

      rawGroups.forEach((item, idx) => {
        if (!item) return;

        const groupName = extractGroupName(item);

        const docListRaw =
          item.groupdocument ||
          item.group_document ||
          item.group_documents ||
          item.groupdocuments ||
          item.groupDocument ||
          item.groupDocuments ||
          item.documents ||
          item.docs ||
          item.group_manager_documents ||
          item.groupManagerDocuments ||
          item.onboarding_documents ||
          item.onboardingDocuments;

        let docList = null;
        if (Array.isArray(docListRaw)) {
          docList = docListRaw;
        } else if (docListRaw && typeof docListRaw === "object") {
          if (
            docListRaw.document_name ||
            docListRaw.document_id ||
            docListRaw.history_id ||
            docListRaw.url ||
            docListRaw.extension ||
            docListRaw.size ||
            docListRaw.id
          ) {
            docList = [docListRaw];
          } else {
            const vals = Object.values(docListRaw);
            if (vals.length > 0 && typeof vals[0] === "object" && vals[0] !== null) {
              docList = vals;
            } else {
              docList = [docListRaw];
            }
          }
        } else if (item.document && typeof item.document === "object") {
          if (Array.isArray(item.document)) {
            docList = item.document;
          } else if (item.document.document_name || item.document.id || item.document.name || item.document.url) {
            docList = [item.document];
          }
        }

        if (docList && Array.isArray(docList) && docList.length > 0) {
          const normalizedDocs = docList.map((doc, dIdx) => {
            const docObj = (doc && typeof doc === "object") ? doc : { name: String(doc) };
            const docName = extractDocumentName(docObj, groupName);
            const isDeleted = Boolean(
              docObj.is_deleted ||
              docObj.is_deleted_by_group ||
              docObj.deleted_by_group ||
              docObj.group_file_status === "deleted" ||
              (docObj.data && docObj.data.is_deleted)
            );
            const deletedAt = docObj.deleted_at || (docObj.data && docObj.data.deleted_at) || null;

            return {
              ...docObj,
              id: docObj.document_id || docObj.id || docObj.history_id || `doc_${idx}_${dIdx}`,
              document_id: docObj.document_id || docObj.id,
              history_id: docObj.history_id,
              acceptance_tracking_id: docObj.acceptance_tracking_id,
              name: docName,
              size: docObj.size ? `${docObj.size} MB` : "",
              extension: docObj.extension || docObj.ext || "",
              status: docObj.status || "accepted",
              is_deleted: isDeleted,
              is_deleted_by_group: isDeleted,
              deleted_by_group: isDeleted,
              deleted_at: deletedAt,
              group_file_status: isDeleted ? "deleted" : (docObj.group_file_status || "active"),
              url: docObj.url || docObj.document_url || docObj.link || docObj.file_url || "",
              created_at: docObj.created_at,
              updated_at: docObj.updated_at,
              acknowledged_at: docObj.updated_at || docObj.created_at || docObj.acknowledged_at || docObj.accepted_at,
              acknowledged: true,
              group_id: item.group_id || item.id,
              group_name: groupName,
            };
          });

          const existingGroup = groups.find(
            (g) => (g.id && item.id && g.id === (item.group_id || item.id)) || g.name === groupName
          );

          if (existingGroup) {
            existingGroup.documents.push(...normalizedDocs);
          } else {
            groups.push({
              id: item.group_id || item.id || `group_${idx}`,
              group_id: item.group_id || item.id,
              name: groupName,
              address: item.address || "",
              phone: item.phone || "",
              email: item.email || "",
              documents: normalizedDocs,
            });
          }
        } else {
          // Flat document fallback
          const docName = extractDocumentName(item, groupName);
          const fallbackGroupName = item.group_name || item.groupname || item.pharmacy_name || "General Agreements";
          let existing = groups.find((g) => g.name === fallbackGroupName);
          if (!existing) {
            existing = {
              id: item.group_id || item.id || `group_${groups.length}`,
              group_id: item.group_id || item.id,
              name: fallbackGroupName,
              address: item.address || "",
              phone: item.phone || "",
              email: item.email || "",
              documents: [],
            };
            groups.push(existing);
          }
          const isDeleted = Boolean(
            item.is_deleted ||
            item.is_deleted_by_group ||
            item.deleted_by_group ||
            item.group_file_status === "deleted" ||
            (item.data && item.data.is_deleted)
          );
          const deletedAt = item.deleted_at || (item.data && item.data.deleted_at) || null;

          existing.documents.push({
            ...item,
            id: item.document_id || item.id || `doc_${existing.documents.length}`,
            document_id: item.document_id || item.id,
            history_id: item.history_id,
            acceptance_tracking_id: item.acceptance_tracking_id,
            name: docName,
            size: item.size ? `${item.size} MB` : "",
            extension: item.extension || item.ext || "",
            status: item.status || "accepted",
            is_deleted: isDeleted,
            is_deleted_by_group: isDeleted,
            deleted_by_group: isDeleted,
            deleted_at: deletedAt,
            group_file_status: isDeleted ? "deleted" : (item.group_file_status || "active"),
            url: item.url || item.document_url || item.link || item.file_url || "",
            created_at: item.created_at,
            updated_at: item.updated_at,
            acknowledged_at: item.updated_at || item.created_at || item.acknowledged_at || item.accepted_at,
            acknowledged: true,
            group_name: fallbackGroupName,
          });
        }
      });

      return groups.filter((g) => g.documents && g.documents.length > 0);
    };

    const fetchAcknowledgedDocuments = async () => {
      isLoading.value = true;
      try {
        const res = await $store.dispatch("GET_ACKNOWLEDGED_DOCUMENTS");
        if (res) {
          groupsList.value = normalizeGroups(res);
        }
      } catch (err) {
        console.warn("Error fetching acknowledged documents:", err);
        try {
          const fallbackRes = await $store.dispatch("GET_GROUP_MANAGER_DOCUMENTS", { acknowledged: 1 });
          if (fallbackRes) {
            groupsList.value = normalizeGroups(fallbackRes);
          }
        } catch (fbErr) {
          console.error("Fallback group manager documents error:", fbErr);
        }
      } finally {
        isLoading.value = false;
      }
    };

    const filteredGroups = computed(() => {
      if (!groupsList.value || groupsList.value.length === 0) return [];
      if (!searchQuery.value || !searchQuery.value.trim()) return groupsList.value;

      const q = searchQuery.value.toLowerCase().trim();
      return groupsList.value
        .map((group) => {
          const groupNameMatch = group.name && group.name.toLowerCase().includes(q);
          const addressMatch = group.address && group.address.toLowerCase().includes(q);
          const emailMatch = group.email && group.email.toLowerCase().includes(q);

          if (groupNameMatch || addressMatch || emailMatch) {
            return group;
          }

          const matchedDocs = group.documents.filter((doc) => {
            return doc.name && doc.name.toLowerCase().includes(q);
          });

          if (matchedDocs.length > 0) {
            return {
              ...group,
              documents: matchedDocs,
            };
          }

          return null;
        })
        .filter(Boolean);
    });

    const totalDocumentsCount = computed(() => {
      if (!filteredGroups.value || filteredGroups.value.length === 0) return 0;
      return filteredGroups.value.reduce((sum, g) => sum + (g.documents ? g.documents.length : 0), 0);
    });

    const openDocPreview = (doc) => {
      selectedDocForReview.value = doc;
      showDocPreviewModal.value = true;
    };

    const isImageDoc = (pathOrExt) => {
      if (!pathOrExt) return false;
      const clean = pathOrExt.split("?")[0].toLowerCase();
      return /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(clean) || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(clean);
    };

    const isPdfDoc = (pathOrExt) => {
      if (!pathOrExt) return false;
      const clean = pathOrExt.split("?")[0].toLowerCase();
      return /\.pdf$/i.test(clean) || clean === 'pdf';
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      try {
        const d = moment(dateStr);
        if (d.isValid()) {
          return d.format("DD MMM YYYY");
        }
      } catch (e) {
        // ignore
      }
      return dateStr;
    };

    const downloadDoc = async (doc, fromModal = false) => {
      if (!doc || !doc.url || doc.url === "=") return;
      try {
        if (fromModal) {
          isDownloadingModalDoc.value = true;
        } else {
          downloadingDocId.value = doc.id || doc.name;
        }

        let extension = doc.extension ? `.${doc.extension}` : "";
        if (!extension) {
          try {
            const path = doc.url.split("?")[0];
            const parts = path.split(".");
            if (parts.length > 1) {
              extension = `.${parts.pop()}`;
            }
          } catch (e) {
            extension = "";
          }
        }

        let fileName = (doc.name || "agreement").replace(/[^a-zA-Z0-9_-]/g, "_");
        if (extension && !fileName.toLowerCase().endsWith(extension.toLowerCase())) {
          fileName += extension;
        }

        const response = await fetch(doc.url);
        if (!response.ok) throw new Error("Network response was not ok");
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        ElMessage({
          type: "success",
          message: "Agreement downloaded successfully!",
        });
      } catch (error) {
        console.warn("Direct blob download failed, falling back to window.open", error);
        window.open(doc.url, "_blank");
      } finally {
        if (fromModal) {
          isDownloadingModalDoc.value = false;
        } else {
          downloadingDocId.value = null;
        }
      }
    };

    onMounted(() => {
      if (setPageClassGlobal) {
        setPageClassGlobal("global_auth_page global_acknowledged_agreements_page");
      }
      fetchAcknowledgedDocuments();
    });

    return {
      isLoading,
      groupsList,
      filteredGroups,
      totalDocumentsCount,
      searchQuery,
      showDocPreviewModal,
      selectedDocForReview,
      downloadingDocId,
      isDownloadingModalDoc,
      goBack,
      openDocPreview,
      isImageDoc,
      isPdfDoc,
      formatDate,
      downloadDoc,
    };
  },
};
</script>

<style lang="scss" scoped>
.global_acknowledged_agreements_page {
  background-color: var(--color-gray-bg, #f8fafc);
  min-height: 100vh;
}

.button_back {
  --color: #334155;
  font-size: 20px;
}

.auth-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;

  .auth-heading {
    h1 {
      font-size: 22px;
      font-weight: 700;
      color: #0f172a;
      margin: 0 0 4px;
      line-height: 1.25;
    }
    .auth-subtext {
      font-size: 13px;
      color: #64748b;
      margin: 0;
      line-height: 1.45;
    }
  }

  .brand-icon {
    flex-shrink: 0;
    margin-left: 12px;
  }
}

.agreements-search-box {
  margin-bottom: 14px;
  :deep(.el-input__inner) {
    border-radius: 10px;
    height: 40px;
    border-color: #e2e8f0;
    font-size: 13px;
    &:focus {
      border-color: #3b82f6;
    }
  }
}

.agreements-stats-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #475569;
  background: #f1f5f9;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 16px;

  .stat-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    i {
      color: #3b82f6;
    }
    strong {
      color: #0f172a;
    }
  }

  .stat-divider {
    color: #cbd5e1;
  }
}

/* Skeleton Loading */
.agreements-loading-wrapper {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;

  .skeleton-group-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 14px;
    border: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    gap: 10px;

    .skeleton-group-header {
      display: flex;
      gap: 10px;
      align-items: center;
      padding-bottom: 10px;
      border-bottom: 1px solid #f1f5f9;

      .skeleton-icon {
        width: 34px;
        height: 34px;
        border-radius: 8px;
        background: #f1f5f9;
      }
      .skeleton-header-text {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 5px;
        .skeleton-title {
          width: 40%;
          height: 12px;
        }
        .skeleton-sub {
          width: 60%;
          height: 9px;
        }
      }
    }

    .skeleton-doc-row {
      display: flex;
      gap: 10px;
      align-items: center;
      .skeleton-doc-icon {
        width: 30px;
        height: 30px;
        border-radius: 6px;
        background: #f1f5f9;
      }
      .skeleton-doc-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        .skeleton-doc-name {
          width: 50%;
          height: 11px;
        }
        .skeleton-doc-meta {
          width: 30%;
          height: 9px;
        }
      }
    }

    .skeleton-line {
      background: #f1f5f9;
      border-radius: 4px;
    }
  }
}

/* Empty State */
.agreements-empty-card {
  background: #ffffff;
  border-radius: 14px;
  padding: 36px 20px;
  text-align: center;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  margin-bottom: 20px;

  .empty-icon-circle {
    width: 60px;
    height: 60px;
    background: #ecfdf5;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
  }

  .empty-title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 6px;
  }

  .empty-subtitle {
    font-size: 13px;
    color: #64748b;
    max-width: 320px;
    margin: 0 auto;
    line-height: 1.45;
  }
}

/* Group Cards List */
.groups-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
}

.group-card {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;

  /* Group Header */
  .group-header {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    padding: 12px 14px;

    .group-header-main {
      display: flex;
      gap: 10px;
      align-items: flex-start;
    }

    .group-avatar-icon {
      width: 34px;
      height: 34px;
      border-radius: 8px;
      background: #eff6ff;
      color: #2563eb;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 17px;
      flex-shrink: 0;
    }

    .group-title-info {
      flex: 1;
      min-width: 0;

      .group-name-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        margin-bottom: 3px;

        .group-name {
          font-size: 15px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
          text-transform: capitalize;
        }

        .group-doc-count-badge {
          font-size: 11px;
          font-weight: 600;
          color: #2563eb;
          background: #dbeafe;
          padding: 1px 7px;
          border-radius: 999px;
          white-space: nowrap;
        }
      }

      .group-meta-row {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        font-size: 11px;
        color: #64748b;

        .group-meta-item {
          display: inline-flex;
          align-items: center;
          gap: 3px;
        }
      }
    }
  }

  /* Group Documents List */
  .group-docs-list {
    display: flex;
    flex-direction: column;
  }

  .doc-item-row {
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    transition: background-color 0.15s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: #fdfdfd;
    }

    .doc-item-main {
      display: flex;
      gap: 10px;
      align-items: center;
      flex: 1;
      min-width: 0;
      cursor: pointer;
    }

    .doc-item-icon {
      width: 34px;
      height: 34px;
      border-radius: 8px;
      background: #eff6ff;
      color: #2563eb;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 17px;
      flex-shrink: 0;

      &.is-pdf {
        background: #fee2e2;
        color: #ef4444;
      }

      &.is-img {
        background: #fdf4ff;
        color: #a855f7;
      }
    }

    .doc-item-info {
      flex: 1;
      min-width: 0;

      .doc-title-row {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 3px;
        flex-wrap: wrap;

        .doc-name {
          font-size: 13.5px;
          font-weight: 600;
          color: #1e293b;
          word-break: break-word;
        }

        .doc-badge-accepted {
          font-size: 11px;
          font-weight: 600;
          color: #059669;
          background: #d1fae5;
          padding: 1px 6px;
          border-radius: 5px;
          display: inline-flex;
          align-items: center;
          gap: 3px;
        }

        .doc-badge-removed {
          font-size: 11px;
          font-weight: 600;
          color: #b45309;
          background: #fef3c7;
          border: 1px solid #fde68a;
          padding: 1px 6px;
          border-radius: 5px;
          display: inline-flex;
          align-items: center;
          gap: 3px;
        }
      }

      .doc-meta-row {
        display: flex;
        align-items: center;
        gap: 5px;
        flex-wrap: wrap;

        .meta-tag {
          font-size: 11px;
          color: #64748b;
          background: #f1f5f9;
          padding: 1px 5px;
          border-radius: 4px;
          display: inline-flex;
          align-items: center;
          gap: 3px;

          &.meta-ext {
            font-weight: 700;
            color: #ef4444;
            background: #fee2e2;
          }

          &.meta-removed-date {
            color: #b45309;
            background: #fef3c7;
            font-weight: 500;
          }
        }
      }
    }

    /* Small Professional Actions */
    .doc-item-actions {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-shrink: 0;

      .btn-doc-small {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        font-size: 12px;
        font-weight: 600;
        padding: 5px 10px;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.15s ease;
        border: 1px solid transparent;
        outline: none;
        white-space: nowrap;

        &.btn-view {
          background: #eff6ff;
          color: #2563eb;
          border-color: #bfdbfe;
          &:hover {
            background: #dbeafe;
          }
        }

        &.btn-download {
          background: #ffffff;
          color: #475569;
          border-color: #cbd5e1;
          &:hover:not(:disabled) {
            background: #f8fafc;
            color: #1e293b;
            border-color: #94a3b8;
          }
          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }
      }
    }
  }
}

/* Modal Styling */
.modal_success {
  :deep(.el-dialog__header) {
    padding: 14px 18px;
    border-bottom: 1px solid #e2e8f0;
    .el-dialog__title {
      font-size: 16px;
      font-weight: 700;
      color: #0f172a;
    }
  }

  :deep(.el-dialog__body) {
    padding: 14px;
  }

  :deep(.el-dialog__footer) {
    padding: 10px 18px;
    border-top: 1px solid #e2e8f0;
  }
}

.doc-modal-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.doc-modal-meta-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;

  .doc-modal-tags {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;

    .doc-badge-accepted {
      font-size: 11px;
      font-weight: 600;
      color: #059669;
      background: #d1fae5;
      padding: 2px 7px;
      border-radius: 5px;
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    .doc-badge-removed {
      font-size: 11px;
      font-weight: 600;
      color: #b45309;
      background: #fef3c7;
      border: 1px solid #fde68a;
      padding: 2px 7px;
      border-radius: 5px;
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    .meta-item {
      font-size: 11px;
      color: #64748b;
      background: #f1f5f9;
      padding: 2px 7px;
      border-radius: 5px;
      display: inline-flex;
      align-items: center;
      gap: 4px;

      &.meta-ext {
        font-weight: 700;
        color: #ef4444;
        background: #fee2e2;
      }
    }
  }

  .doc-modal-header-actions {
    display: flex;
    align-items: center;
    gap: 6px;

    .btn-modal-action {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 11.5px;
      font-weight: 600;
      padding: 5px 10px;
      border-radius: 6px;
      border: 1px solid #cbd5e1;
      background: #ffffff;
      color: #334155;
      text-decoration: none;
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover:not(:disabled) {
        background: #f8fafc;
        border-color: #94a3b8;
      }
    }
  }
}

.doc-modal-removed-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-left: 4px solid #f59e0b;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 12px;
  color: #92400e;
  line-height: 1.45;
  margin-top: 4px;
  margin-bottom: 6px;

  i {
    font-size: 16px;
    color: #f59e0b;
    margin-top: 1px;
    flex-shrink: 0;
  }

  .notice-text {
    flex: 1;
    strong {
      color: #78350f;
    }
  }
}

.doc-inline-preview-wrapper {
  width: 100%;
  height: 52vh;
  min-height: 300px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  overflow: hidden;

  .doc-preview-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .doc-preview-iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  .doc-preview-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.doc-no-preview-available {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 36px 16px;
  color: #94a3b8;
  i {
    font-size: 36px;
    margin-bottom: 6px;
  }
  p {
    margin: 0;
    font-size: 13px;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
}
</style>
